import React, { useEffect, useState } from 'react';

import Cancel from '@mui/icons-material/CancelRounded';
import CheckCircle from '@mui/icons-material/CheckCircleRounded';
import LockReset from '@mui/icons-material/LockResetRounded';
import {
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import Button from '../../../../components/Button';
import CommitForm from '../../../../components/CommitForm';
import ContentCard from '../../../../components/ContentCard';
import Modal from '../../../../components/Modal';
import { MFAMethod } from '../../../../services/auth/types';
import {
  useDisableUserMutation,
  useEnableUserMutation,
  useGetUserStatusQuery,
  useResetTemporaryPasswordMutation,
  useUpdateUserMfaMethodMutation,
} from '../../../../services/users';
import useError from '../../../../utils/react/hooks/useError';
import useSuccess from '../../../../utils/react/hooks/useSuccess';
import { StyledBackdrop } from '../../index.styles';

const ACCOUNT_ACCESS_FIELDS = {
  enabled: {
    xs: 12,
  },
  creationDatetime: {
    xs: 12,
  },
  accountStatus: {
    xs: 12,
  },
  mfaMethod: {
    xs: 12,
  },
};

interface AccountAccessCardProps {
  user?: {
    id: string,
  },
}

const AccountAccessCard = ({ user }: AccountAccessCardProps) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [isTotpSetupWarningOpen, setIsTotpSetupWarningOpen] = useState<boolean>(false);

  const {
    data: userStatus,
    isLoading: isUserStatusLoading,
    isError: isUserStatusError,
    error: userStatusError,
  } = useGetUserStatusQuery({ userId: user?.id || '' }, { skip: !user?.id });

  const [disableUser, {
    isSuccess: isDisableUserSuccess,
    isLoading: isDisableUserLoading,
    isError: isDisableUserError,
    error: disableUserError,
  }] = useDisableUserMutation();
  const [enableUser, {
    isSuccess: isEnableUserSuccess,
    isLoading: isEnableUserLoading,
    isError: isEnableUserError,
    error: enableUserError,
  }] = useEnableUserMutation();
  const [resetTemporaryPassword, {
    isSuccess: isResetTemporaryPasswordSuccess,
    isLoading: isResetTemporaryPasswordLoading,
    isError: isResetTemporaryPasswordError,
    error: resetTemporaryPasswordError,
  }] = useResetTemporaryPasswordMutation();
  const [updateUserMfaMethod, {
    isSuccess: isUpdateUserMfaMethodSuccess,
  }] = useUpdateUserMfaMethodMutation();

  useEffect(() => {
    if (userStatus?.data) {
      setIsEnabled(userStatus.data.enabled);
    }
  }, [userStatus?.data]);

  useSuccess(isDisableUserSuccess, 'User disabled');
  useSuccess(isEnableUserSuccess, 'User enabled');
  useSuccess(isResetTemporaryPasswordSuccess, 'Password updated');
  useSuccess(isUpdateUserMfaMethodSuccess, 'MFA method updated');

  useError(isUserStatusError, userStatusError);
  useError(isDisableUserError, disableUserError);
  useError(isEnableUserError, enableUserError);
  useError(isResetTemporaryPasswordError, resetTemporaryPasswordError);

  const handleMfaChange = (newMfaMethod: MFAMethod) => {
    if (user?.id) {
      updateUserMfaMethod({ userId: user.id, mfaMethod: newMfaMethod });
    }
  };

  if (!user || isUserStatusLoading) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <>
      <StyledBackdrop open={isDisableUserLoading || isEnableUserLoading}>
        <CircularProgress />
      </StyledBackdrop>
      <ContentCard variant="outlined" title="Account Access">
        <Stack spacing={2} direction="column">
          <CommitForm
            data={userStatus?.data}
            metadata={userStatus?.metadata}
            fields={{
              ...ACCOUNT_ACCESS_FIELDS,
              ...(userStatus?.data?.temporaryPassword ? {
                temporaryPassword: {
                  xs: 12,
                },
              } : {}),
            }}
            onChange={(values) => {
              if (values?.mfaMethod === MFAMethod.TOTP) {
                setIsTotpSetupWarningOpen(true);
              }
            }}
            onSubmit={(values) => {
              if (values?.mfaMethod) {
                handleMfaChange(values.mfaMethod);
              }
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack>
                {isEnabled ? (
                  <Button
                    color="error"
                    startIcon={<Cancel fontSize="small" />}
                    onClick={() => disableUser({ userId: user.id })}
                  >
                    Disable User
                  </Button>
                ) : (
                  <Button
                    color="success"
                    startIcon={<CheckCircle fontSize="small" />}
                    onClick={() => enableUser({ userId: user.id })}
                  >
                    Enable User
                  </Button>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack>
                <Button
                  startIcon={<LockReset />}
                  onClick={() => resetTemporaryPassword({ userId: user?.id })}
                  loading={isResetTemporaryPasswordLoading}
                >
                  Reset Temporary Password
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </ContentCard>
      <Modal
        open={isTotpSetupWarningOpen}
        onClose={() => setIsTotpSetupWarningOpen(false)}
        hideCloseIcon
        title="Warning"
        buttons={[
          {
            label: 'OK',
            onClick: () => setIsTotpSetupWarningOpen(false),
          },
        ]}
      >
        <Typography>
          To enable MFA based on an authenticator app,
          the user will have to sign out, then sign back in.{' '}
          <u>After entering their MFA code in via SMS one last time</u>, they will then
          be prompted to set up their authenticator app.
        </Typography>
      </Modal>
    </>
  );
};

export default AccountAccessCard;
